<template>
  <main
    id="fatturazione-page"
    class="page-container"
    v-if="$route.name === 'fatturazione'"
  >
    <header class="fatt-header">
      <h1>Fatturazione</h1>
      <p class="fatt-hint">
        Le fatture elettroniche emesse dagli ordini, con lo stato dell'invio.
      </p>
    </header>

    <div class="fatt-toolbar">
      <div class="fatt-filters">
        <div class="field">
          <span>Stato</span>
          <StatusSelect
            v-model="filtri.stato"
            :options="opzioniStato"
            placeholder="Tutti"
            placeholderIcon="fa-filter"
            @update:modelValue="ricarica()"
          />
        </div>

        <div class="field">
          <span>Anno</span>
          <StatusSelect
            v-model="filtri.anno"
            :options="opzioniAnno"
            placeholder="Tutti"
            placeholderIcon="fa-calendar"
            @update:modelValue="ricarica()"
          />
        </div>

        <label class="field">
          <span>Cliente</span>
          <input
            type="search"
            v-model.trim="filtri.cliente"
            placeholder="Cerca per nome"
            @input="ricercaCliente"
          />
        </label>

        <button
          v-if="filtriAttivi"
          type="button"
          class="btn-secondary"
          @click="azzeraFiltri"
        >
          Togli i filtri
        </button>
      </div>

      <div v-if="righe.length > 0" class="pagination fatt-pagination">
        <div class="pagination-right">
          <div class="pagination-info">{{ conteggio }}</div>

          <div class="pagination-controls">
            <button
              class="icons-button"
              type="button"
              :disabled="isLoading || pagina === 1"
              title="Torna alla prima pagina"
              @click="vaiAPagina(1)"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>

            <button
              class="icons-button"
              type="button"
              :disabled="isLoading || pagina === 1"
              title="Torna alla pagina precedente"
              @click="vaiAPagina(pagina - 1)"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>

            <button
              v-for="numero in pagineVisibili"
              :key="numero"
              type="button"
              class="pages-button"
              :class="{ active: numero === pagina }"
              @click="vaiAPagina(numero)"
            >
              {{ numero }}
            </button>

            <button
              class="icons-button"
              type="button"
              :disabled="isLoading || pagina === pagine"
              title="Vai alla pagina successiva"
              @click="vaiAPagina(pagina + 1)"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>

            <button
              class="icons-button"
              type="button"
              :disabled="isLoading || pagina === pagine"
              title="Vai all'ultima pagina"
              @click="vaiAPagina(pagine)"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <p v-if="isLoading" class="fatt-state">Caricamento…</p>
    <p v-else-if="error" class="fatt-state fatt-state--error">{{ error }}</p>

    <EmptyState
      v-else-if="righe.length === 0"
      icon="fa-file-invoice-dollar"
      title="Nessuna fattura"
      :text="
        filtriAttivi
          ? 'Nessuna fattura con questi filtri.'
          : 'Qui compariranno le fatture elettroniche emesse dagli ordini.'
      "
    />

    <div v-else class="tbl-wrap">
      <table class="tbl">
        <thead>
          <tr>
            <th>Numero</th>
            <th>Data</th>
            <th>Cliente</th>
            <th>Totale</th>
            <th>Stato</th>
            <th>Tentativo</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="riga in righe" :key="riga.id">
            <td data-label="Numero">
              <router-link
                v-if="dettaglioDisponibile"
                :to="{
                  name: 'fatturazione-dettaglio',
                  params: { invoiceId: riga.id },
                }"
              >
                {{ riga.numero || "—" }}
              </router-link>
              <span v-else>{{ riga.numero || "—" }}</span>
            </td>
            <td data-label="Data">{{ formatDate(riga.date) }}</td>
            <td data-label="Cliente">{{ riga.cliente || "—" }}</td>
            <td data-label="Totale">{{ formatTotale(riga.total) }}</td>
            <td data-label="Stato">
              <span
                class="invoice-badge"
                :class="`invoice-badge--${riga.status}`"
              >
                {{ etichettaStato(riga.status) }}
              </span>
            </td>
            <td data-label="Tentativo">{{ riga.attempt ?? "—" }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </main>
  <!-- Il dettaglio di una fattura si apre qui, come in Ordini. -->
  <router-view></router-view>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import EmptyState from "@/components/EmptyState.vue";
import StatusSelect from "@/components/StatusSelect.vue";
import { testoConteggio } from "@/utilities/conteggioPagine";
import { ETICHETTE_STATO } from "@/utilities/fatture";
import { formatPlatformDate } from "@/utilities/platformFormat";

/** L'API serve 20 righe per pagina. */
const RIGHE_PER_PAGINA = 20;

/** Attesa prima di interrogare l'API mentre si scrive il nome del cliente. */
const ATTESA_RICERCA_MS = 350;

/**
 * [sc-49222] L'elenco delle fatture elettroniche dell'azienda, con filtri per
 * stato, anno e cliente. Struttura gemella del Log attività.
 *
 * Il dettaglio è la rotta figlia `fatturazione-dettaglio`; se manca, il
 * numero resta testo.
 */
export default {
  name: "FatturazionePage",
  components: { EmptyState, StatusSelect },
  data() {
    return {
      righe: [],
      anni: [],
      pagina: 1,
      pagine: 1,
      totale: 0,
      isLoading: true,
      error: "",
      filtri: { stato: "", anno: "", cliente: "" },
      timerRicerca: null,
      // Numero dell'ultima richiesta partita: una risposta con un numero
      // diverso arriva fuori ordine e si scarta.
      richiesta: 0,
    };
  },
  computed: {
    filtriAttivi() {
      return Boolean(
        this.filtri.stato || this.filtri.anno || this.filtri.cliente
      );
    },
    opzioniStato() {
      return [
        { value: "", label: "Tutti" },
        ...Object.entries(ETICHETTE_STATO).map(([value, label]) => ({
          value,
          label,
        })),
      ];
    },
    opzioniAnno() {
      return [
        { value: "", label: "Tutti" },
        ...this.anni.map((a) => ({ value: String(a), label: String(a) })),
      ];
    },
    /** La rotta del dettaglio c'è dal task 5: prima, niente link. */
    dettaglioDisponibile() {
      return this.$router.hasRoute("fatturazione-dettaglio");
    },
    conteggio() {
      const primo = (this.pagina - 1) * RIGHE_PER_PAGINA + 1;
      return testoConteggio({
        totalItems: this.totale,
        startItem: primo,
        endItem: Math.min(primo + this.righe.length - 1, this.totale),
      });
    },
    /** Due pagine prima e due dopo quella aperta, come in Clienti. */
    pagineVisibili() {
      const intorno = 2;
      const inizio = Math.max(1, this.pagina - intorno);
      const fine = Math.min(this.pagine, this.pagina + intorno);
      return Array.from({ length: fine - inizio + 1 }, (_, i) => inizio + i);
    },
  },
  watch: {
    // [sc-49222] Il dettaglio resta aperto sopra l'elenco, che non si
    // smonta: al ritorno si rilegge la pagina in cui si era, perché dopo una
    // verifica o un reinvio lo stato è cambiato.
    "$route.name"(nuovo, vecchio) {
      if (nuovo === "fatturazione" && vecchio === "fatturazione-dettaglio")
        this.ricarica(this.pagina);
    },
  },
  async created() {
    await this.ricarica();
  },
  beforeUnmount() {
    clearTimeout(this.timerRicerca);
  },
  methods: {
    async ricarica(numero = 1) {
      const mio = ++this.richiesta;
      this.isLoading = true;
      this.error = "";
      try {
        const params = { pagina: numero };
        if (this.filtri.stato) params.stato = this.filtri.stato;
        if (this.filtri.anno) params.anno = this.filtri.anno;
        if (this.filtri.cliente) params.cliente = this.filtri.cliente;
        const { data } = await axiosInstance.get("/invoices/list", { params });
        if (mio !== this.richiesta) return;
        this.righe = Array.isArray(data?.righe) ? data.righe : [];
        this.pagina = data?.pagina ?? 1;
        this.pagine = data?.pagine ?? 1;
        this.totale = data?.totale ?? 0;
        if (Array.isArray(data?.anni)) this.anni = data.anni;
      } catch (err) {
        if (mio !== this.richiesta) return;
        this.righe = [];
        this.error = err?.response?.data?.message || "Fatture non disponibili.";
      } finally {
        if (mio === this.richiesta) this.isLoading = false;
      }
    },
    ricercaCliente() {
      clearTimeout(this.timerRicerca);
      this.timerRicerca = setTimeout(() => this.ricarica(), ATTESA_RICERCA_MS);
    },
    vaiAPagina(numero) {
      const voluta = Math.min(Math.max(1, numero), this.pagine);
      if (voluta === this.pagina || this.isLoading) return;
      this.ricarica(voluta);
    },
    azzeraFiltri() {
      clearTimeout(this.timerRicerca);
      this.filtri = { stato: "", anno: "", cliente: "" };
      this.ricarica();
    },
    etichettaStato(stato) {
      return ETICHETTE_STATO[stato] || stato || "—";
    },
    formatDate(iso) {
      return iso ? formatPlatformDate(iso) : "—";
    },
    formatTotale(valore) {
      const n = Number(valore);
      if (valore === null || valore === undefined || Number.isNaN(n))
        return "—";
      return n.toLocaleString("it-IT", {
        style: "currency",
        currency: "EUR",
      });
    },
  },
};
</script>

<style scoped>
/* `.page-container` ha `margin: 0 auto`: in una colonna flex il margine
   automatico toglie lo stretch e la pagina prende la larghezza del
   contenuto (cambia coi filtri, si allarga con l'XML). Tutto schermo. */
.page-container {
  width: 100%;
  padding: 24px;
}

.fatt-header h1 {
  margin: 0 0 4px;
  font-size: 20px;
}

.fatt-hint {
  margin: 0 0 16px;
  max-width: 60ch;
  color: #555;
  font-size: 14px;
}

.fatt-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px 24px;
  margin-bottom: 16px;
}

.fatt-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
}

.fatt-filters .field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
}

.fatt-pagination {
  display: flex;
  justify-content: flex-end;
  margin: 0;
  width: auto;
}

.fatt-state {
  margin: 12px 0;
  color: #555;
  font-size: 14px;
}

.fatt-state--error {
  color: #b00020;
}

/* Lo scorrimento sta dentro il riquadro, non nella pagina (vedi Log
   attività: sotto i 1023px `body` taglia l'overflow orizzontale). */
.tbl-wrap {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.tbl {
  min-width: 36rem;
}

/* Il badge di stato è lo stesso di Ordini (OrdiniPage.css, globale perché la
   pagina è nel router senza lazy loading); qui solo il minimo per non
   dipendere dall'ordine di caricamento dei fogli. */
.invoice-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}
.invoice-badge--da_verificare {
  background: #fff7ed;
  color: #c2410c;
}
.invoice-badge--inviata {
  background: #eff6ff;
  color: #1d4ed8;
}
.invoice-badge--consegnata,
.invoice-badge--in_cassetto {
  background: #f0fdf4;
  color: #15803d;
}
.invoice-badge--scartata {
  background: #fef2f2;
  color: #b91c1c;
}

@media (max-width: 700px) {
  .page-container {
    padding: 16px;
  }

  .fatt-filters {
    flex-direction: column;
    align-items: stretch;
  }

  /* Sei colonne su un telefono non ci stanno: ogni riga diventa una scheda,
     con l'etichetta portata dalla cella (`data-label`). */
  .tbl-wrap {
    border: none;
    background: transparent;
    box-shadow: none;
    overflow-x: visible;
  }

  .tbl {
    min-width: 0;
  }

  .tbl thead {
    display: none;
  }

  .tbl,
  .tbl tbody,
  .tbl tr,
  .tbl td {
    display: block;
    width: 100%;
    box-sizing: border-box;
  }

  .tbl tr {
    margin-bottom: 10px;
    padding: 12px 14px;
    border: 0.5px solid var(--color-border, #d4d4d8);
    border-radius: 10px;
    background: var(--color-surface, #fff);
  }

  .tbl td {
    padding: 4px 0;
    border: none;
  }

  .tbl td::before {
    content: attr(data-label);
    display: block;
    color: var(--color-text-muted, #71717a);
    font-size: 12px;
  }
}
</style>
